import { useRef, useState, useCallback } from 'react'
import { useOption } from '@react-aria/listbox'
import { ListState } from '@react-stately/list'
import cx from 'classnames'

import Styles from './PreviewAccountOption.module.scss'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { RoundedPlatformIcon } from 'components/RoundedPlatformIcon/RoundedPlatformIcon'
import { AccountAvatar } from '../../AccountToggle/AccountAvatar'

interface Props {
  node: import('@react-types/shared').Node<ExternalIntegration>
  state: ListState<ExternalIntegration>
  image?: string
  variant?: 'default' | 'avatar'
}

const VERTICAL_PLATFORMS = [ExternalIntegrationEnum.Instagram]

export function PreviewAccountOption({ node, state, image, variant = 'default' }: Props) {
  const ref = useRef<HTMLLIElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })

  const { optionProps, isSelected, isFocused } = useOption({ key: node.key }, state, ref)

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    setStartPos({ x: e.clientX, y: e.clientY })
    setIsDragging(false)
  }, [])

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const deltaX = Math.abs(e.clientX - startPos.x)
      const deltaY = Math.abs(e.clientY - startPos.y)

      if (deltaX > 5 || deltaY > 5) {
        setIsDragging(true)
      }
    },
    [startPos],
  )

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) {
        state.selectionManager.select(node.key)
      }
      setIsDragging(false)
    },
    [isDragging, node.key, state.selectionManager],
  )

  if (!node.value) return null
  const integration = node.value
  const imgSrc = image ?? integration.picture

  return (
    <li
      {...optionProps}
      ref={ref}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      data-testid="preview-account-option"
      className={cx(Styles.option, {
        [Styles.selected]: isSelected,
        [Styles.focused]: isFocused,
        [Styles.vertical]: VERTICAL_PLATFORMS.includes(integration.provider),
        [Styles.horizontal]: !VERTICAL_PLATFORMS.includes(integration.provider),
        [Styles.avatar]: variant === 'avatar',
      })}
      style={
        variant === 'default'
          ? { background: `url(${imgSrc})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : undefined
      }
    >
      {variant === 'default' ? (
        <div className={Styles.channelIcon}>
          <RoundedPlatformIcon platform={integration.provider} size={16} />
        </div>
      ) : (
        <AccountAvatar account={integration} size="lg" />
      )}
    </li>
  )
}
